import MapComponent from '@/components/map-component';
import BottomSheet from '@/components/ui/bottom-sheet';
import { useRouter, useLocation } from '@/lib/router-helpers';
import { useBookingStore } from '@/store/booking-store';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crosshair, Map as MapIcon } from 'lucide-react';

type BranchForMap = { id: string; name: string; subtitle: string };

export default function MapPage() {
  const [sheetHeight, setSheetHeight] = useState(360);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const lastYRef = useRef(0);
  const router = useRouter();
  const location = useLocation();
  const resetBookingState = useBookingStore((s) => s.resetBookingState);
  const initialBranch = (location.state as { selectedBranch?: BranchForMap } | null)?.selectedBranch ?? null;

  const handleExit = () => {
    resetBookingState();
    router.push('/organization/dars');
  };

  const getSnapPositions = () => {
    if (!containerRef.current) return { bottom: 150, middle: 360, top: 780 };
    const containerHeight = containerRef.current.clientHeight;
    return {
      bottom: 150,
      middle: Math.round(containerHeight * 0.45),
      top: containerHeight - 80,
    };
  };

  useEffect(() => {
    const positions = getSnapPositions();
    setSheetHeight(positions.middle);
    const onResize = () => setSheetHeight(getSnapPositions().middle);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const snapToNearest = (currentHeight: number) => {
    const positions = getSnapPositions();
    const snapValues = [positions.bottom, positions.middle, positions.top];

    const nearest = snapValues.reduce((prev, curr) =>
      Math.abs(curr - currentHeight) < Math.abs(prev - currentHeight) ? curr : prev
    );

    animateToHeight(nearest);
  };

  const animateToHeight = (targetHeight: number) => {
    const startHeight = sheetHeight;
    const duration = 400;
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 5);
      const newHeight = startHeight + (targetHeight - startHeight) * easeProgress;
      setSheetHeight(newHeight);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      if (e instanceof TouchEvent) e.preventDefault();
      const clientY = e instanceof TouchEvent ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const deltaY = clientY - startYRef.current;
      const newHeight = Math.max( getSnapPositions().bottom, Math.min(getSnapPositions().top, startHeightRef.current - deltaY) );
      lastYRef.current = clientY;
      setSheetHeight(newHeight);
    };

    const handleEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;

      const positions = getSnapPositions();
      const sortedSnaps = [positions.bottom, positions.middle, positions.top].sort((a, b) => a - b);
      
      const startSnap = sortedSnaps.reduce((prev, curr) => 
        Math.abs(curr - startHeightRef.current) < Math.abs(prev - startHeightRef.current) ? curr : prev
      );

      const proximitySnap = sortedSnaps.reduce((prev, curr) =>
        Math.abs(curr - sheetHeight) < Math.abs(prev - sheetHeight) ? curr : prev
      );

      const dragDistance = lastYRef.current - startYRef.current;
      const threshold = 50;
      
      let thresholdSnap = startSnap;
      if (dragDistance < -threshold) {
         thresholdSnap = sortedSnaps.find(s => s > startSnap) || startSnap;
      } else if (dragDistance > threshold) {
         thresholdSnap = [...sortedSnaps].reverse().find(s => s < startSnap) || startSnap;
      }

      let targetHeight = proximitySnap;
      if (dragDistance < 0) {
          targetHeight = Math.max(thresholdSnap, proximitySnap);
      } else if (dragDistance > 0) {
          targetHeight = Math.min(thresholdSnap, proximitySnap);
      }

      animateToHeight(targetHeight);
    };

    window.addEventListener('mousemove', handleMove as EventListener);
    window.addEventListener('touchmove', handleMove as EventListener, { passive: false });
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove as EventListener);
      window.removeEventListener('touchmove', handleMove as EventListener);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [sheetHeight]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in e) (e as React.TouchEvent).preventDefault?.();
    isDraggingRef.current = true;
    const clientY = 'touches' in e ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
    startYRef.current = clientY;
    lastYRef.current = clientY;
    startHeightRef.current = sheetHeight;
  };

  const handleSnapRequest = (position: 'top' | 'middle' | 'bottom') => {
    const positions = getSnapPositions();
    if (position === 'top') animateToHeight(positions.top);
    else if (position === 'middle') animateToHeight(positions.middle);
    else if (position === 'bottom') animateToHeight(positions.bottom);
  };

  return (
    <div ref={containerRef} className="w-full h-[100dvh] bg-white relative overflow-hidden">
      <MapComponent mapHeight="100%" />

      <div
        style={{ right: 16, bottom: sheetHeight + 20 }}
        className="absolute z-50 flex flex-col gap-4 items-end transition-all duration-200 ease-out pointer-events-none"
      >
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            if (!("geolocation" in navigator)) return
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const lat = pos.coords.latitude
                const lng = pos.coords.longitude
                window.dispatchEvent(new CustomEvent("locateUser", { detail: { lat, lng } }))
              },
              () => {},
              { enableHighAccuracy: true, timeout: 10000 }
            )
          }}
          aria-label="Center on current location"
          className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg border border-gray-100 text-gray-700 hover:text-pink-600 transition-colors pointer-events-auto"
        >
          <Crosshair className="w-6 h-6" />
        </motion.button>
    
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleExit}
          className="group flex items-center gap-3 bg-white rounded-full shadow-lg pl-5 pr-2 py-2 border border-gray-100 pointer-events-auto"
          aria-label="Exit"
        >
          <span className="text-base font-bold text-gray-900">Exit</span>
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center transition-colors">
            <MapIcon className="w-6 h-6 text-pink-600" />
          </div>
        </motion.button>
      </div>

      <motion.div 
        style={{ height: sheetHeight }} 
        className="absolute left-0 right-0 bottom-0 z-50 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]"
      >
        <BottomSheet
          height={sheetHeight}
          onDragStart={handleDragStart}
          onSnapRequest={handleSnapRequest}
          initialBranch={initialBranch}
        />
      </motion.div>
    </div>
  );
}
